import classNames from 'classnames';
import React from 'react';
import uniqid from 'uniqid';
import { useParams, useNavigate } from 'react-router-dom';

import GridMatrixCell from 'containers/admin/clients/client/tabs-components/goals-tab/goals-matrix/goals-matrix-cell/goals-matrix-cell';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import { Loader } from 'ui/loader';

import type { Goal } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface GoalsMatrixProps {
  headers: string[];
  rows: string[];
  data: Goal[];
  showOnlyDefined: boolean;
  year: string | number;
  loading: boolean;
}

interface CellInput {
  id?: string | number;
  type: string;
  category: string;
}

const ROW_HEADER_SX = {
  borderWidth: '1px',
  borderRightWidth: 0,
  borderColor: 'tokens.navy.200',
  p: 1,
  fontWeight: 600,
};

const CELL_SX = {
  borderWidth: '1px',
  borderColor: 'tokens.navy.200',
  cursor: 'pointer',
};

const GoalsMatrix = ({
  headers,
  rows,
  data,
  showOnlyDefined,
  year,
  loading,
}: GoalsMatrixProps): React.JSX.Element => {
  const { id: clientId } = useParams<{ id: string }>();

  const navigate = useNavigate();

  const getCellData = (row: string, header: string): Goal[] =>
    data.filter((item) => item.category === row && item.type === header);

  const handleCellClick = (cell: CellInput): void => {
    const goalId = cell.id ? '' : 'new';
    const type = cell.type.replace(' ', '-').toLowerCase();
    const category = cell.category.replace(' ', '-').toLowerCase();

    navigate(
      `/admin/clients/${clientId}/goals/${year}/${type}/${category}/${goalId}`
    );
  };

  const filteredHeaders = showOnlyDefined
    ? headers.filter((header) => data.some((item) => item.type === header))
    : headers;

  const filteredRows = showOnlyDefined
    ? rows.filter((row) => data.some((item) => item.category === row))
    : rows;

  const isTableEmpty =
    filteredRows.length === 0 || filteredHeaders.length === 0;

  const headerBasicStyles =
    'p-3 bg-navy-50 text-navy-500 font-semibold text-xs text-left tracking-widest';

  const gridTemplateColumns = `195px repeat(${filteredHeaders.length}, minmax(0, 1fr))`;

  return (
    <Stack sx={{ overflowX: 'auto', flex: '1 1 0%' }}>
      <Loader loading={loading} fullScreen={false} sx={{ opacity: 0.6 }} />
      {isTableEmpty ? (
        <EmptySpaceContent
          customClass="flex-1"
          text="There is no goal to display yet."
        />
      ) : (
        <table
          className="w-full border-separate border-spacing-0 mobile:min-w-max sm:min-w-full"
          data-testid="goals-matrix-table"
        >
          <thead data-testid="goals-matrix-table-header">
            <tr
              className="grid mb-2"
              style={{
                gridTemplateColumns,
              }}
            >
              <th className={`rounded-l-md ${headerBasicStyles}`} scope="col">
                {'Goal Category'.toUpperCase()}
              </th>
              {filteredHeaders.map((header, i) => (
                <th
                  key={header}
                  className={classNames(
                    'border-r border-white',
                    headerBasicStyles,
                    {
                      'rounded-r-md !border-r-0':
                        i === filteredHeaders.length - 1,
                      'border-l': i === 0,
                    }
                  )}
                  scope="col"
                >
                  {header.toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="border border-navy-200">
            {filteredRows.map((row, rowIndex) => (
              <tr
                key={uniqid()}
                className={classNames('grid border-spacing-0', {
                  'rounded-t-md': rowIndex === 0,
                  'rounded-b-md': rowIndex === filteredRows.length - 1,
                })}
                style={{
                  gridTemplateColumns,
                }}
              >
                <Box
                  component="th"
                  sx={[
                    ROW_HEADER_SX,
                    rowIndex === 0 && { borderTopLeftRadius: '0.375rem' },
                    rowIndex === filteredRows.length - 1 && {
                      borderBottomLeftRadius: '0.375rem',
                    },
                    rowIndex !== 0 && { borderTopWidth: 0 },
                  ]}
                  scope="row"
                >
                  {row}
                </Box>
                {filteredHeaders.map((header, i) => {
                  const cellData = getCellData(row, header);
                  const isLastColumn = i === filteredHeaders.length - 1;

                  return (
                    <Box
                      component="td"
                      key={header}
                      sx={[
                        CELL_SX,
                        isLastColumn &&
                          rowIndex === 0 && {
                            borderTopRightRadius: '0.375rem',
                          },
                        isLastColumn &&
                          rowIndex === filteredRows.length - 1 && {
                            borderBottomRightRadius: '0.375rem',
                          },
                        rowIndex !== 0 && { borderTopWidth: 0 },
                        i !== 0 && { borderLeftWidth: 0 },
                      ]}
                    >
                      <GridMatrixCell
                        cellData={cellData}
                        onClick={handleCellClick}
                        row={row}
                        header={header}
                      />
                    </Box>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Stack>
  );
};

export default GoalsMatrix;
